// @ts-nocheck
import React, { useEffect } from 'react';
import {
  IconButton,
  Box,
  Grid,
  Typography,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { handleDownload } from 'pages/Forms/RegisterForm/CustomFormBuilder/components/UploadAttachments/AttachmentUtils';
import useAttachment from 'pages/Forms/RegisterForm/CustomFormBuilder/components/UploadAttachments/hooks/useAttachment';
import {
  HelpIcon,
  ImageIcon,
} from 'pages/Forms/RegisterForm/CustomFormBuilder/config/constantSvg';
import {
  fileFormats,
  imageFormats,
} from 'pages/Forms/RegisterForm/CustomFormBuilder/components/UploadAttachments/CommonAttachment/constants';
import FileTypeIcon from 'pages/Forms/RegisterForm/CustomFormBuilder/components/UploadAttachments/CommonAttachment/FileAttachment/FileIconType';
import GenericToolTip from 'components/__v2__/GenericToolTip';
import Loader from 'components/atoms/Loader';
import { useToast } from 'hooks';
import { AddIcon, DeleteIcon, DownloadIcon } from 'assets/icons';
import { useAttachmentStyles } from '../../../../../components/UploadAttachments/CommonAttachment/style';
import { useNoteAndAttachmentFieldStyles } from '../../../ImageUploadFormFieldPreview/components/ImageAttachments/styles';
import {
  FIELD_TYPES,
  generateUniqueId,
} from 'pages/Forms/RegisterForm/CustomFormBuilder/config/constant';

const AttachmentInput = ({
  inputId,
  inputLabel,
  inputLabelStyle,
  attachmentType,
  handleAttachment,
}) => {
  const classes = useNoteAndAttachmentFieldStyles();

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleAttachment({ target: { files: e.dataTransfer.files } });
  };

  return (
    <Box
      className={classes.attachmentContainer}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <label htmlFor={inputId} className={classes.attachmentContainerText}>
        <AddIcon fontSize="small" color="action" />
        <span className={classes.dropFileText} style={inputLabelStyle}>
          {inputLabel}
        </span>
      </label>
      <input
        type="file"
        id={inputId}
        multiple
        className={classes.fileInput}
        onChange={handleAttachment}
        {...(attachmentType === FIELD_TYPES.IMAGES && {
          accept: imageFormats.map((format) => `.${format}`).join(','),
        })}
      />
    </Box>
  );
};

const AttachmentUploader = ({
  content,
  isClientSideUpload = false,
  attachmentKey = 'attachments',
  attachmentAlignment = 'row',
  attachmentType = 'files',
  inputLabel = 'Click/Drag here to upload files',
  inputLabelStyle,
  handleUpdateAttachments,
}) => {
  const classes = useAttachmentStyles();
  const toast = useToast();

  const {
    attachmentLoading,
    fetchAttachmentData,
    saveAttachment,
    getFormData,
  } = useAttachment();

  const attachments = content[attachmentKey] || [];

  const handleSuccess = (data) => {
    const newAttachments = attachments.concat(
      data.map(({ id, file, name, mime_type, thumbnail }) => ({
        id,
        file,
        name,
        mime_type,
        thumbnail,
      })),
    );
    handleUpdateAttachments(newAttachments);
  };

  const handleFailure = () => {
    toast.error('File could not be saved. Please try again !');
  };

  const handleDeleteAttachment = (attachmentIndex) => {
    const newAttachments = attachments.filter(
      (_, index) => index !== attachmentIndex,
    );
    handleUpdateAttachments(newAttachments);
  };

  const handleAttachToPdfChange = (attachmentIndex) => {
    const newAttachments = attachments.map((attachment, index) =>
      index === attachmentIndex
        ? { ...attachment, attach_to_pdf: !attachment.attach_to_pdf }
        : attachment,
    );
    handleUpdateAttachments(newAttachments);
  };

  const handleDowloadFile = (name, url) => {
    const fileExt = name?.split('.', 2)[1];

    const isExcelFile =
      fileExt === 'xlsx' ||
      fileExt === 'xlsm' ||
      fileExt === 'xlsb' ||
      fileExt === 'xls';

    isExcelFile ? handleDownloadExcel(url) : handleDownload(url);
  };

  const handleAttachment = (e) => {
    const files = e.target.files;
    if (files.length < 0) return;

    const isFileNameTooLong = Array.from(files).some(
      (file) => file.name?.length > 100,
    );

    if (isFileNameTooLong) {
      toast.error('File name should not exceed 100 characters.');
      return;
    }

    const isFileSizeExceeded = Array.from(files).some(
      (file) => file.size > 25 * 1024 * 1024,
    );

    if (isFileSizeExceeded) {
      toast.error('File size should not exceed 25MB.');
      return;
    }

    isClientSideUpload ? handleClientUpload(files) : handleServerUpload(files);
  };

  const handleServerUpload = (files) => {
    saveAttachment(
      getFormData(files),
      null,
      null,
      handleSuccess,
      handleFailure,
    );
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleClientUpload = async (files) => {
    const selectedFiles = Array.from(files);
    const filesWithBase64 = await Promise.all(
      selectedFiles.map(async (file) => {
        const base64 = await convertToBase64(file);
        return {
          id: generateUniqueId(),
          file: base64,
          name: file.name,
          mime_type: file.type,
        };
      }),
    );

    const newAttachments = attachments.concat(filesWithBase64);
    handleUpdateAttachments(newAttachments);
  };

  useEffect(() => {
    if (!isClientSideUpload && attachments.length > 0) {
      const attachmentIds = attachments.map((attachment) => attachment.id);
      fetchAttachmentData({
        query: { attachments: attachmentIds },
        skip: false,
        onSuccess: (data) => {
          const newAttachments = Object.keys(data).map((itemId) => ({
            id: itemId,
            ...data[itemId],
          }));
          handleUpdateAttachments(newAttachments);
        },
      });
    }
  }, []);

  return (
    <Box>
      <AttachmentInput
        inputId={content?.id}
        inputLabel={inputLabel}
        inputLabelStyle={inputLabelStyle}
        attachmentType={attachmentType}
        handleAttachment={handleAttachment}
      />
      {attachmentLoading ? (
        <Loader />
      ) : (
        attachments.length > 0 && (
          <Box mt={2} display="flex" flexDirection={attachmentAlignment}>
            {attachments.map((attachment, index) => {
              const ext = attachment?.name?.split('.')?.pop()?.toLowerCase();
              const isFile = fileFormats.includes(ext);
              const isImage = imageFormats.includes(ext);

              return (
                <Box
                  key={attachment.id}
                  className={classes.multiAttachmentContainer}
                >
                  <Box className={classes.attachmentsContainer}>
                    <Box>
                      <Box className={classes.attachmentIcon}>
                        {isImage ? <ImageIcon /> : null}

                        {isFile ? (
                          <FileTypeIcon fileType={ext} classes={classes} />
                        ) : null}
                      </Box>

                      {isImage ? (
                        <img
                          src={attachment?.file}
                          alt={attachment?.name}
                          key={attachment}
                          className={classes.attachment}
                        />
                      ) : null}

                      {isFile ? (
                        <Box className={classes.attachment}>
                          <p className={classes.fileName}>
                            <GenericToolTip title={attachment?.name} char={20}>
                              {attachment?.name}
                            </GenericToolTip>
                          </p>
                        </Box>
                      ) : null}

                      <Box className={classes.onHover}>
                        <p className={classes.imageName}>{attachment?.name}</p>
                        <Box className={classes.iconButtonContainer}>
                          <IconButton
                            className={classes.actionIconButton}
                            aria-label="download"
                            onClick={() => {
                              handleDowloadFile(
                                attachment.name,
                                attachment.file,
                              );
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: '12px' }} />
                          </IconButton>
                        </Box>
                        <Box className={classes.iconButtonContainer}>
                          <IconButton
                            className={classes.actionIconButton}
                            aria-label="delete"
                            onClick={() => handleDeleteAttachment(index)}
                          >
                            <DeleteIcon sx={{ fontSize: '12px' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {attachment?.mime_type.includes('pdf') &&
                    content.type === FIELD_TYPES.INSTRUCTION && (
                      <Grid mt={1} display="flex" alignItems="center">
                        <Grid ml={-1}>
                          <Checkbox
                            size="small"
                            checked={attachment.attach_to_pdf}
                            onChange={() => handleAttachToPdfChange(index)}
                          />
                        </Grid>
                        <Typography variant="subtitle2" color="inherit">
                          Attach in response pdf
                        </Typography>
                        <Tooltip
                          title="Ensure this document is attached to the response Pdf when downloading the submitted response."
                          placement="top"
                        >
                          <span style={{ paddingLeft: '5px' }}>{HelpIcon}</span>
                        </Tooltip>
                      </Grid>
                    )}
                </Box>
              );
            })}
          </Box>
        )
      )}
    </Box>
  );
};

export default AttachmentUploader;
